import React, { useState, useEffect, useContext } from 'react'
import DayPicker, { DateUtils } from 'react-day-picker'
import classNames from 'classnames'
import { ReactComponent as ArrowLeft } from 'assets/icons/arrow-left.svg'
import { ReactComponent as ArrowRight } from 'assets/icons/arrow-right.svg'
import { getFormattedDate, getMonthRange, dayDiff } from 'utils/dateHelper'
import { isEmpty } from 'utils/browserHelper'
import 'react-day-picker/lib/style.css'
import './_calendar.scss'
import langTranslate from 'locale'
import { DOMAIN } from 'constants/base'
import { DOMAIN_COUNTRY_MAP } from 'constants/flightResults'
import { getCurrencyByLocale } from 'utils/currencyHelper'
import Clevertap from 'analytics'

const Calendar = ({
    selectedDates,
    className,
    title,
    roundTrip,
    onClickCalendar,
    priceData,
    activeOnward,
    activeReturn,
    isRtl,
    isMulticity,
    multiCityDestinations,
    index,
    fromMonth,
    toMonth,
    ...otherProps
}) => {
    const NO_OF_MONTHS = 2

    const today = fromMonth ? fromMonth : new Date()
    toMonth = toMonth ? toMonth : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    const [selectedDate, setSelectedDate] = useState(selectedDates['from'])
    const { locale } = getCurrencyByLocale()
    const [range, setRange] = useState(selectedDates)
    const dayPickerClassName = classNames(className, {
        Selectable: roundTrip && !isRtl,
        'Selectable--rtl': isRtl && roundTrip
    })
    const { from, to, enteredTo } = range
    const modifiers = { start: from, end: enteredTo, leftEdge: { daysOfWeek: [1] }, rightEdge: { daysOfWeek: [0] } }
    const disabledDays = {
        before:
            isMulticity && index > 1 && !isEmpty(multiCityDestinations[`depart_date_${index - 1}`])
                ? new Date(
                    getFormattedDate(multiCityDestinations[`depart_date_${index - 1}`], 'MM/DD/YYYY', 'DD/MM/YYYY')
                )
                : activeReturn && range.from
                    ? range.from
                    : today,
        after: toMonth
    }
    const { fareCalendar = {}, directionClick } = otherProps

    useEffect(() => {
        setRange(selectedDates)
    }, [selectedDates])

    function isSelectingFirstDay(from, to, day) {
        if (roundTrip && activeReturn) {
            const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from)
            const isRangeSelected = from && to
            return !isBeforeFirstDay || isRangeSelected
        }
        if (isMulticity) {
            // && index > 1 && !isEmpty(multiCityDestinations[`depart_date_${index - 1}`])) {
            const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from)
            const isRangeSelected = from && to
            return !isBeforeFirstDay || isRangeSelected
        }
        return false
    }

    function handleDayMouseEnter(day) {
        const { from, to } = range
        if (isSelectingFirstDay(from, to, day)) {
            setRange({ ...range, enteredTo: day })
        }
    }
    function handleDayClick(day) {
        if (roundTrip && activeOnward) {
            if (from && to) {
                let diff = dayDiff(day, to)
                if (diff >= 1) {
                    setSelectedDate(day)
                    onClickCalendar({
                        from: day,
                        to: day,
                        enteredTo: day
                    })
                } else {
                    setSelectedDate(day)
                    onClickCalendar({
                        ...range,
                        from: day
                    })
                }
            } else if (from && isEmpty(to)) {
                setSelectedDate(day)
                onClickCalendar({
                    ...range,
                    from: day
                })
            }
            Clevertap.event('Air_UI_Action', {
                action_type: 'BUTTON',
                action_name: 'SRP_OW_Date_Selected'
            })
        } else if (activeReturn) {
            Clevertap.event('Air_UI_Action', {
                action_type: 'BUTTON',
                action_name: 'SRP_RT_Date_Selected'
            })
            setSelectedDate(day)
            onClickCalendar({
                ...range,
                to: day,
                enteredTo: day
            })
        } else {
            Clevertap.event('Air_UI_Action', {
                action_type: 'BUTTON',
                action_name: 'SRP_OW_Date_Selected'
            })
            setSelectedDate(day)
            onClickCalendar({
                from: day,
                to: null,
                enteredTo: null
            })
        }
    }
    function nextClick(onNextClick, previousMonth) {
        let monthRange = getMonthRange(previousMonth, 'right')
        directionClick(monthRange)
        onNextClick()
    }
    function previousClick(onPreviousClick, previousMonth) {
        let monthRange = getMonthRange(previousMonth, 'left')
        directionClick(monthRange)
        onPreviousClick()
    }
    function Navbar({ onPreviousClick, onNextClick, previousMonth, nextMonth }) {
        return (
            <>
                <div className="flex flex-middle flex-between p-absolute w-100p px-5">
                    <div className={classNames('flex-1 ta-left', { flip: isRtl })}>
                        {(previousMonth.getFullYear() === today.getFullYear() &&
                            previousMonth.getMonth() < today.getMonth()) ||
                            (previousMonth.getFullYear() < today.getFullYear() &&
                                previousMonth.getMonth() > today.getMonth()) ? (
                            <ArrowLeft height={24} width={24} className="c-neutral-200" />
                        ) : (
                            <ArrowLeft
                                data-testid="leftArrow"
                                height={24}
                                width={24}
                                className="c-pointer"
                                onClick={() => previousClick(onPreviousClick, previousMonth)}
                            />
                        )}
                    </div>

                    <div className={classNames('flex-1 ta-right', { flip: isRtl })}>
                        {nextMonth.getTime() > toMonth.getTime() ? (
                            <ArrowRight
                                height={24}
                                width={24}
                                style={{ alignSelf: 'flex-end' }}
                                className="c-neutral-200"
                            />
                        ) : (
                            <ArrowRight
                                data-testid="rightArrow"
                                height={24}
                                width={24}
                                style={{ alignSelf: 'flex-end' }}
                                className="c-pointer"
                                onClick={() => nextClick(onNextClick, previousMonth)}
                            />
                        )}
                    </div>
                </div>
            </>
        )
    }

    const renderDay = day => {
        const date = day.getDate()
        const lastday = new Date(day.getFullYear(), day.getMonth() + 1, 0).getDate()
        const formattedDate = getFormattedDate(day, 'DD-MM-YYYY')
        const fullMonth = getFormattedDate(day, 'DD-MMMM-YYYY')
        const month = fullMonth.split('-')[1]
        const fareData = fareCalendar.onward ? fareCalendar.onward[formattedDate] : null
        const cheapFare = fareCalendar.cheapFare ? fareCalendar.cheapFare : null

        return (
            <div
                className={classNames('Day-grid flex flex-middle flex-column', roundTrip ? 'flex-center' : 'flex-top')}
            >
                <div
                    className={classNames(
                        'p-1 day-gridContent fs-14 fw-500 flex flex-middle flex-column flex-center',
                        roundTrip ? 'Round-trip' : 'flex-top',
                        date === 1 ? 'monthStart' : '',
                        date === lastday ? 'monthEnd' : ''
                    )}
                    style={{
                        height: '45px'
                    }}
                >
                    {date}

                    {((!activeReturn && !roundTrip) || DOMAIN_COUNTRY_MAP[DOMAIN] === 'IN') && fareData &&
                        fareData.pr !== undefined &&
                        fareData.pr !== '-' && (
                            <div
                                className={classNames(
                                    'fs-1 Datepicker--price',
                                    cheapFare[month] === fareData.pr ? 'c-success-500' : 'c-neutral-400'
                                )}
                            >
                                {fareData.pr}
                            </div>
                        )}
                    {((!activeReturn && !roundTrip) || DOMAIN_COUNTRY_MAP[DOMAIN] === 'IN') && fareData && fareData.pr == '-' && (
                        <div className="fs-1 Datepicker--price c-neutral-400">-</div>
                    )}
                </div>
            </div>
        )
    }
    const MONTHS = [
        langTranslate('January'),
        langTranslate('February'),
        langTranslate('March'),
        langTranslate('April'),
        langTranslate('May'),
        langTranslate('June'),
        langTranslate('July'),
        langTranslate('August'),
        langTranslate('September'),
        langTranslate('October'),
        langTranslate('November'),
        langTranslate('December')
    ]
    const weekSort = [
        langTranslate('Su'),
        langTranslate('Mo'),
        langTranslate('Tu'),
        langTranslate('We'),
        langTranslate('Th'),
        langTranslate('Fr'),
        langTranslate('Sa')
    ]
    const FIRST_DAY_OF_WEEK = {
        IN: 1,
        AE: 0,
        SA: 0,
        KW: 0,
        BH: 0,
        QA: 0,
        OM: 0,
        ME: 0
    }
    return (
        <>
            <DayPicker
                {...otherProps}
                numberOfMonths={NO_OF_MONTHS}
                interactionDisabled={true}
                renderDay={renderDay}
                className={dayPickerClassName}
                weekdaysShort={weekSort}
                firstDayOfWeek={FIRST_DAY_OF_WEEK[locale]}
                disabledDays={disabledDays}
                fromMonth={today}
                toMonth={toMonth}
                modifiers={modifiers}
                locale="it"
                months={MONTHS}
                month={
                    isMulticity && index > 1 && !isEmpty(multiCityDestinations[`depart_date_${index - 1}`])
                        ? new Date(
                            getFormattedDate(
                                multiCityDestinations[`depart_date_${index - 1}`],
                                'MM/DD/YYYY',
                                'DD/MM/YYYY'
                            )
                        )
                        : range['from']
                } // This is to be change wrt return / onward selection
                selectedDays={roundTrip ? [from, { from, to: enteredTo }] : selectedDate}
                navbarElement={<Navbar />}
                onDayClick={day => handleDayClick(day)}
                onDayMouseEnter={day => handleDayMouseEnter(day)}
            />
        </>
    )
}

Calendar.defaultProps = {
    selectedDates: [] || '',
    className: '',
    title: '',
    roundTrip: false,
    onClickCalendar: () => { }
}

export default Calendar
